import { useEffect, useMemo, useRef, useState } from "react";
import Konva from "konva";
import { FooterButton } from "../footer";
import styles from "./zoom-controls.module.css";
import ZoomMenu from "./zoom-menu";
import { fitToStage } from "./fit-to-stage";
import { useOnClickOutside } from "usehooks-ts";
import { Point } from "frontend/utils/math-utils";
import { useIsCIMReorderEnabled } from "utils/url-utils";
interface ZoomInputProps {
  scale: number;
  onScaleChange: (scale: number, newScale: number) => void;
  onZoom: (scale: number, position: Point) => void;
  stageRef?: React.RefObject<Konva.Stage>;
}

export default function ZoomInput({ scale, onScaleChange, onZoom, stageRef }: ZoomInputProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string | number>(Math.round(scale * 100));
  const [isFocused, setIsFocused] = useState(false);
  const zoomButtonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isCimReorderEnabled = useIsCIMReorderEnabled();

  function toggleMenu() {
    setIsMenuOpen((previous) => !previous);
  }

  function handleScaleChange(newScale: number) {
    onScaleChange(scale, newScale);
    setInputValue(Math.round(newScale * 100));
    setIsMenuOpen(false);
  }

  function handleZoomToFit() {
    const viewBox = fitToStage(stageRef);
    if (viewBox) {
      onZoom(viewBox.scale, {
        x: viewBox.x,
        y: viewBox.y,
      });
    }
    setIsMenuOpen(false);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value.replace("%", "");
    if (value === "" || /^[0-9]*$/.test(value)) {
      setInputValue(value === "" ? "" : Number.parseInt(value));
    }
  }

  function updateZoomValue(value: string | number) {
    if (value === "") {
      setInputValue(Math.round(scale * 100));
      return;
    }
    const numericValue = typeof value === "string" ? Number.parseInt(value.replace("%", "")) : value;
    const clampedValue = Math.min(Math.max(numericValue, 1), 400);
    setInputValue(clampedValue);
    onScaleChange(scale, clampedValue / 100);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      updateZoomValue(inputValue);
      e.currentTarget.blur();
      setIsMenuOpen(false);
    }
  }

  function handleBlur() {
    updateZoomValue(inputValue);
    setIsFocused(false);
  }

  useEffect(() => {
    setInputValue(Math.round(scale * 100));
  }, [scale]);

  useOnClickOutside(menuRef, () => {
    setIsMenuOpen(false);
  });

  const InputSelector = useMemo(() => {
    const ZoomInput = (
      <input
        type="text"
        role="spinbutton"
        value={isFocused ? inputValue : `${inputValue}%`}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={() => setIsFocused(true)}
        onPaste={(e) => e.preventDefault()}
        pattern="[0-9]{1,3}"
        maxLength={isFocused ? 3 : 4}
        className={styles.zoomInputField}
      />
    );

    if (!isCimReorderEnabled) {
      return (
        <FooterButton testid="zoom-input-button" ref={zoomButtonRef} styles={[styles.zoomInput]} onClick={toggleMenu}>
          {ZoomInput}
        </FooterButton>
      );
    }

    return (
      <div data-testid="zoom-input-button" ref={zoomButtonRef} className={styles.cimZoomInput} onClick={toggleMenu}>
        <div className={styles.cimZoomInputContent}>{ZoomInput}</div>
      </div>
    );
  }, [handleBlur, handleKeyDown, inputValue, isCimReorderEnabled, isFocused]);

  return (
    <>
      {InputSelector}
      {isMenuOpen && (
        <ZoomMenu
          menuRef={menuRef}
          zoomButtonRef={zoomButtonRef}
          onScaleChange={handleScaleChange}
          onZoomToFit={handleZoomToFit}
          variant={isCimReorderEnabled ? "cim" : "default"}
        />
      )}
    </>
  );
}
